import { useEffect, useState } from 'react';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import AdministrativePage from '../../modules/organization/administrative/AdministrativePage/AdministrativePage';
import SkillPage from '../../modules/organization/administrative/skillPage';
import ErpTicketsPage from '../../modules/organization/administrative/erpTickets/ErpTicketsPage';
import ExpenseClaimsPage from '../../modules/organization/administrative/expenses/ExpenseClaimsPage';
import UserSettings from '../../modules/organization/administrative/userSettings';
import AffairPage from '../../modules/crm/affairs/AffairPage/AffairPage';
import AffairDetailPage from '../../modules/crm/affairs/pages/AffairDetailPage';
import ProspectingPage from '../../modules/crm/prospecting/pages/ProspectingPage';
import ProspectingDetailPage from '../../modules/crm/prospecting/pages/ProspectingDetailPage';
import CrmDashboardPage from '../../modules/crm/dashboard/CrmDashboardPage';
import BadgerPage from '../../modules/time-tracking/BadgerPage/BadgerPage';
import DashboardPage from '../dashboard/DashboardPage/DashboardPage';
import GedPage from '../../modules/documents/ged/gedPage';
import InterventionCreationPage from '../../modules/interventions/pages/InterventionCreationPage';
import InterventionDetailPage from '../../modules/interventions/pages/InterventionDetailPage';
import InterventionListPage from '../../modules/interventions/InterventionListPage/InterventionListPage';
import LdaPage from '../../modules/lda/LdaPage/LdaPage';
import PartnerCreation from '../../modules/crm/partners/partnerCreation';
import PartnerList from '../../modules/crm/partners/partnerList';
import PartnerPage from '../../modules/crm/partners/PartnerPage/PartnerPage';
import PlanningPage from '../../modules/organization/planning/PlanningPage/PlanningPage';
import PowerBalancePage from '../../modules/power-balance/PowerBalancePage/PowerBalancePage';
import EquipmentPage from '../../modules/equipment/EquipmentPage/EquipmentPage';
import ManufacturerCatalogPage from '../../modules/equipment/ManufacturerCatalogPage/ManufacturerCatalogPage';
import ProjectList from '../../modules/projects/projectList';
import ProjectPage from '../../modules/projects/ProjectPage/ProjectPage';
import QuotePage from '../../modules/quotes/QuotePage/QuotePage';
import LaborProfileSettingsPage from '../../modules/quotes/LaborProfileSettingsPage';
import DocumentTemplatesDemoPage from '../../modules/hr/documents/demo/DocumentTemplatesDemoPage';
import MyRHPage from '../../modules/hr/MyRHPage/MyRHPage';
import StockPage from '../../modules/stock/stockPage';
import { getUserTasks } from '../../modules/organization/tasks/tasks.api';
import TasksPage from '../../modules/organization/tasks/tasksPage';
import WikiPage from '../../modules/knowledge/wiki/wikiPage';
import { apiFetch, clearAuthSession, parseApiResponse } from '../../utils/apiClient';
import GlobalSearchPage from '../../features/global-search/pages/GlobalSearchPage';
import MainLayoutFrame from './MainLayoutFrame';

const readCurrentUser = () => {
  try { return JSON.parse(localStorage.getItem('currentUser') || '{}'); } catch { return {}; }
};

function MainLayout() {
  const navigate = useNavigate();
  const [userTasks, setUserTasks] = useState([]);
  const [currentUser, setCurrentUser] = useState(readCurrentUser);

  useEffect(() => {
    apiFetch('/api/users/me')
      .then(parseApiResponse)
      .then((user) => {
        localStorage.setItem('currentUser', JSON.stringify(user));
        setCurrentUser(user);
      })
      .catch((error) => {
        if (error.status !== 401) console.error('Failed to refresh current user:', error);
      });
  }, []);

  useEffect(() => {
    const loadUserTasks = () => {
      const user = readCurrentUser();
      if (user.id) {
        getUserTasks(user.id).then(setUserTasks)
          .catch((error) => console.error('Failed to load tasks:', error));
      }
    };
    loadUserTasks();
    window.addEventListener('sas-erp-tasks-changed', loadUserTasks);
    return () => window.removeEventListener('sas-erp-tasks-changed', loadUserTasks);
  }, []);

  useEffect(() => {
    const handleAuthExpired = () => navigate('/login');
    window.addEventListener('sas-erp-auth-expired', handleAuthExpired);
    return () => window.removeEventListener('sas-erp-auth-expired', handleAuthExpired);
  }, [navigate]);

  const handleLogout = () => {
    clearAuthSession();
    navigate('/login');
  };

  return <MainLayoutFrame currentUser={currentUser} userTasks={userTasks} onLogout={handleLogout}>
          <Routes>
            <Route path="/" element={<DashboardPage />} />
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/search" element={<GlobalSearchPage />} />
            <Route path="/projets" element={<ProjectList />} />
            <Route path="/interventions" element={<InterventionListPage />} />
            <Route path="/interventions/creation" element={<InterventionCreationPage />} />
            <Route path="/interventions/:id" element={<InterventionDetailPage />} />
            <Route path="/interventions/:id/lda" element={<LdaPage />} />
            <Route path="/affaires" element={<AffairPage />} />
            <Route path="/affaires/:id" element={<AffairDetailPage />} />
            <Route path="/prospection" element={<ProspectingPage />} />
            <Route path="/prospection/:id" element={<ProspectingDetailPage />} />
            <Route path="/crm" element={<CrmDashboardPage />} />
            <Route path="/projets/creation" element={<Navigate to="/affaires" replace />} />
            <Route path="/projets/:id/lda" element={<LdaPage />} />
            <Route path="/projets/:id/devis" element={<QuotePage />} />
            <Route path="/projets/:id/bilan-puissance" element={<PowerBalancePage />} />
            <Route path="/projets/:id/materiel" element={<EquipmentPage />} />
            <Route path="/projets/:id" element={<ProjectPage />} />
            <Route path="/devis" element={<QuotePage />} />
            <Route path="/planning" element={<PlanningPage />} />
            <Route path="/ged" element={<GedPage />} />
            <Route path="/partenaires" element={<PartnerList />} />
            <Route path="/partenaires/creation" element={<PartnerCreation />} />
            <Route path="/partenaires/:id" element={<PartnerPage />} />
            <Route path="/wiki/*" element={<WikiPage />} />
            <Route path="/stock" element={<StockPage />} />
            <Route path="/materiel/catalogue" element={<ManufacturerCatalogPage />} />
            <Route path="/temps" element={<BadgerPage />} />
            <Route path="/saisietemps/:projectId" element={<BadgerPage />} />
            <Route path="/rh" element={<MyRHPage />} />
            <Route path="/rh/documents/demo" element={<DocumentTemplatesDemoPage />} />
            <Route path="/administratif" element={<AdministrativePage currentUser={currentUser} />} />
            <Route path="/administratif/competences" element={<SkillPage currentUser={currentUser} />} />
            <Route path="/administratif/utilisateurs" element={<UserSettings />} />
            <Route path="/administratif/couts-horaires" element={<LaborProfileSettingsPage />} />
            <Route path="/administratif/notes-de-frais" element={<ExpenseClaimsPage />} />
            <Route path="/administratif/suivi-erp/*" element={<ErpTicketsPage />} />
            <Route path="/tasks" element={<TasksPage />} />
          </Routes>
  </MainLayoutFrame>;
}

export default MainLayout;
